import * as Sentry from '@sentry/react';
import { Navigate } from 'react-router-dom';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useBooking } from '@features/booking/state';
import { usePaymentFee } from '@features/booking/hooks/usePaymentFee';
import { StripePaymentForm } from '@features/booking/components/payment/StripePaymentForm';
import { MercadoPagoPaymentForm } from '@features/booking/components/payment/MercadoPagoPaymentForm';

const PaymentPage = () => {
  const restaurant = useRestaurant();
  const { isLoading } = useBooking();
  const { feeBreakdown } = usePaymentFee();

  // BookingProvider hydrates from sessionStorage in an effect (see
  // state/BookingProvider.tsx Effect 1) — on the first paint of a hard
  // refresh / auth-bounce return / 3DS challenge resume, `selectedSlot`
  // is still null and `usePaymentFee` falls into its WL branch,
  // returning `guaranteeType: null`. Without this gate the redirect
  // below would fire before hydration completes and silently boot a
  // valid in-flight deposit user back to `/`. Render nothing until
  // hydration settles; sessionStorage read is synchronous and `useEffect`
  // resolves in the same microtask, so this is a single-frame skip in
  // practice. The form mount cost is also avoided during loading, which
  // is the existing behavior on every booking page.
  if (isLoading) {
    return null;
  }

  // Route-level guard. `/payment` is meaningful only when the user holds a
  // payment-bearing guarantee (deposit | no_show). Without it, the form
  // mounts, collects card details, then hard-fails at submit (the
  // MercadoPagoPaymentForm.tsx:182 / StripePaymentForm sibling guard) —
  // wasting the user's time and creating a conversion leak. Direct URL
  // access can land here three ways: stale sessionStorage from a prior
  // session, browser back-navigation after a slot type change, or
  // deep-link reload after auth bounce. Redirect to the landing page so
  // the booking flow re-derives the correct next step. The form-level
  // submit guard stays as defense-in-depth.
  const requiresPayment =
    feeBreakdown.guaranteeType === 'deposit' || feeBreakdown.guaranteeType === 'no_show';

  if (!requiresPayment) {
    // info-level capture — replaces the visibility we lose by preventing
    // users from reaching the form-level captureException at
    // MercadoPagoPaymentForm.tsx:182. Same context the form guard
    // emits, plus `route` so we can distinguish the two firing points
    // in Sentry.
    Sentry.captureMessage('PaymentPage guard: redirecting non-payment slot', {
      level: 'info',
      tags: {
        guard: 'payment-route-traditional-slot',
        'restaurant.slug': restaurant.subdomainPrefix,
      },
      extra: {
        feeBreakdownGuaranteeType: feeBreakdown.guaranteeType,
        feeBreakdownTotal: feeBreakdown.total,
        restaurantId: restaurant.id,
        paymentProcessor: restaurant.paymentProcessor,
      },
    });
    return <Navigate to="/" replace />;
  }

  const isMercadoPago = restaurant.paymentProcessor === 'mercadopago';

  if (isMercadoPago) {
    return <MercadoPagoPaymentForm />;
  }

  if (!restaurant.publicKeyPayment) {
    return (
      <div className="text-center text-red-500 py-8">
        Payment configuration missing. Please contact the restaurant.
      </div>
    );
  }

  return <StripePaymentForm />;
};

export default PaymentPage;
